import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { imageUrl } from 'app/frontend/base/helpers'
import dayjs from 'dayjs'
import type ItemQuality from 'app/frontend/types/custom/ItemQuality'
import { ActivityTimelineItem } from 'app/frontend/types/custom/ActivityTimelineItem'

interface ActivityTimelineProps {
  hideLabels?: boolean
  timeline: ActivityTimelineItem[]
}

const ActivityTimeline = ({ timeline, hideLabels }: ActivityTimelineProps) => {
  if (!timeline || timeline.length === 0) {
    return null
  }

  const firstTimestamp = new Date(timeline[0].from).getTime()
  const lastTimestamp = new Date(timeline[timeline.length - 1].to).getTime()
  const totalDuration = lastTimestamp - firstTimestamp

  const qualityBackgroundClasses: Record<ItemQuality, string> = {
    poor: 'bg-quality-poor',
    common: 'bg-quality-common',
    uncommon: 'bg-quality-uncommon',
    rare: 'bg-quality-rare',
    epic: 'bg-quality-epic',
    legendary: 'bg-quality-legendary',
    artifact: 'bg-quality-artifact',
    heirloom: 'bg-quality-heirloom',
  }

  return (
    <div>
      <div className='flex h-4 gap-0.5 border border-card bg-card rounded-full'>
        {timeline.map((segment, i) => {
          const startYear = dayjs(segment.from).format('YYYY')
          const endYear = dayjs(segment.to).format('YYYY')
          const segmentStart = new Date(segment.from).getTime()
          const segmentEnd = new Date(segment.to).getTime()
          const segmentDuration = segmentEnd - segmentStart
          const widthPercentage = (segmentDuration / totalDuration) * 100

          const daysBeforeEnd = segment.timestamp
            ? dayjs(segment.overlapping_to).diff(
                dayjs(segment.timestamp),
                'days',
              )
            : 0
          const iconName = segment.icon.replaceAll('-', '')

          return (
            <Tooltip
              key={`${segment.from}-${segment.to}`}
              disableHoverableContent
            >
              <TooltipTrigger asChild>
                <div
                  className={`min-w-px ${
                    qualityBackgroundClasses[
                      segment.timestamp ? segment.quality : 'poor'
                    ]
                  } cursor-pointer ${
                    segment.timestamp
                      ? 'hover:opacity-80'
                      : 'opacity-30 hover:opacity-20'
                  } transition-opacity ${i === 0 ? 'rounded-l-full' : ''} ${
                    i === timeline.length - 1 ? 'rounded-r-full' : ''
                  }`}
                  style={{ width: `${widthPercentage}%` }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className='space-y-2 mb-1'>
                  <div className='text-xs text-muted-foreground pb-2 border-b border-border flex justify-between gap-4'>
                    <span>{segment.title}</span>
                    <span>
                      {startYear === endYear
                        ? startYear
                        : `${startYear} - ${endYear}`}
                    </span>
                  </div>
                  <div className='flex gap-3 items-center'>
                    <img
                      className={`w-8 h-8 border-2 min-w-8 min-h-8 border-[#666] rounded-md ${
                        segment.timestamp ? '' : 'grayscale'
                      }`}
                      alt={segment.name}
                      src={imageUrl(`achievement-icons/${iconName}.jpg`)}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = `https://render.worldofwarcraft.com/us/icons/36/${iconName}.jpg`
                      }}
                    />
                    <div className='flex flex-col'>
                      <div
                        className={`font-medium ${
                          segment.timestamp
                            ? `text-quality-${segment.quality}`
                            : 'text-muted-foreground'
                        }`}
                      >
                        {segment.name}
                      </div>
                      <div className='text-xs'>
                        {!segment.timestamp ? (
                          <span className='italic text-muted-foreground'>
                            Not completed during the{' '}
                            {segment.unreliable_timestamp
                              ? 'expansion'
                              : 'tier'}
                          </span>
                        ) : segment.unreliable_timestamp ? (
                          <span className='italic'>
                            {i === 0
                              ? 'Completed before Dec 5, 2006'
                              : 'Completed before Nov 13, 2008'}
                          </span>
                        ) : (
                          <span>
                            Completed {Math.abs(daysBeforeEnd)}{' '}
                            {Math.abs(daysBeforeEnd) === 1 ? 'day' : 'days'}{' '}
                            {daysBeforeEnd < 0 ? 'after' : 'before'} the{' '}
                            {segment.name.includes('Hall of Fame')
                              ? 'hall of fame closed'
                              : 'end of the tier'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {segment.character_specific &&
                  (!segment.unreliable_timestamp || !segment.timestamp) ? (
                    <div className='text-xs italic text-muted-foreground max-w-[20rem]'>
                      This achievement timestamp is character bound, the user
                      may have completed it{' '}
                      {segment.timestamp ? 'earlier ' : ''}on another character.
                    </div>
                  ) : null}
                </div>
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>

      {!hideLabels && (
        <div className='flex justify-between text-xs text-muted-foreground mt-1'>
          <span>{dayjs(timeline[0].from).format('YYYY')}</span>
          <span>{dayjs().format('YYYY')}</span>
        </div>
      )}
    </div>
  )
}

export default ActivityTimeline
